import { motion } from "framer-motion";
import { Phone, Mail, Clock, MapPin, Languages, Navigation } from "lucide-react";
import { PHONE_DISPLAY, PHONE_HREF, WA_HREF, EMAIL_HREF, OWNER_EMAIL, WhatsAppIcon } from "@/lib/site";
import { T, useLang } from "@/lib/i18n";

const FACT_ICONS = [Clock, Languages, MapPin];
const MAP_EMBED = "https://maps.google.com/maps?q=Salzburg,%20Austria&z=12&output=embed";

const CITIES = [
  "Salzburg", "Wien", "München", "Zürich", "Innsbruck", "Hallstatt", "Berlin", "Genf",
  "Graz", "Linz", "Hamburg", "Basel", "Stuttgart", "Luzern", "Kitzbühel", "Zell am See",
  "Bern", "Berchtesgaden", "Klagenfurt", "Luzern", "Passau", "Chur", "Bregenz", "Königssee",
];

export default function Contact() {
  const { lang } = useLang();
  const t = T[lang].contact;

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-36 overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        <div className="absolute -inset-10 flex flex-wrap items-center content-center justify-center gap-x-10 gap-y-7 opacity-[0.08] -rotate-3">
          {CITIES.map((c, i) => (
            <span key={`${c}-${i}`} className={`font-serif-display italic whitespace-nowrap text-stone-300 ${i % 3 === 0 ? "text-4xl lg:text-5xl" : i % 3 === 1 ? "text-2xl lg:text-3xl" : "text-3xl lg:text-4xl"}`}>
              {c}
            </span>
          ))}
        </div>
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-[#C5A059] mb-4">{t.overline}</p>
          <h2 className="font-serif-display font-semibold tracking-tight text-stone-100 text-3xl sm:text-4xl lg:text-5xl">
            {t.h2a}<em className="not-italic text-[#C5A059]">{t.h2b}</em>
          </h2>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <motion.a
            data-testid="contact-call-card"
            href={PHONE_HREF}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            whileHover={{ y: -6, scale: 1.04 }}
            className="rounded-3xl bg-[#C5A059] text-[#141312] p-8 flex flex-col items-center gap-4 shadow-lg shadow-[#C5A059]/20"
          >
            <span className="w-14 h-14 rounded-full bg-black/10 border border-black/15 flex items-center justify-center">
              <Phone className="w-6 h-6" />
            </span>
            <span className="text-sm text-stone-800/80">{t.call}</span>
            <span className="font-serif-display text-xl font-semibold tracking-wide text-center">{PHONE_DISPLAY}</span>
          </motion.a>

          <motion.a
            data-testid="contact-whatsapp-card"
            href={WA_HREF}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.08, ease: "easeOut" }}
            whileHover={{ y: -6, scale: 1.04 }}
            className="rounded-3xl bg-[#25D366] text-white p-8 flex flex-col items-center gap-4 shadow-lg shadow-emerald-600/25"
          >
            <span className="w-14 h-14 rounded-full bg-white/15 border border-white/30 flex items-center justify-center">
              <WhatsAppIcon className="w-6 h-6" />
            </span>
            <span className="text-sm text-white/85">{t.whatsapp}</span>
            <span className="font-serif-display text-xl font-semibold tracking-wide text-center">{PHONE_DISPLAY}</span>
          </motion.a>

          <motion.a
            data-testid="contact-email-card"
            href={EMAIL_HREF}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.16, ease: "easeOut" }}
            whileHover={{ y: -6, scale: 1.04 }}
            className="rounded-3xl bg-[#D64545] text-white p-8 flex flex-col items-center gap-4 shadow-lg shadow-red-900/25"
          >
            <span className="w-14 h-14 rounded-full bg-white/15 border border-white/30 flex items-center justify-center">
              <Mail className="w-6 h-6" />
            </span>
            <span className="text-sm text-white/85">E-Mail</span>
            <span className="font-serif-display text-lg font-semibold tracking-wide text-center break-all">{OWNER_EMAIL}</span>
          </motion.a>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
          className="mt-12 flex flex-wrap justify-center gap-x-10 gap-y-4"
          data-testid="contact-facts"
        >
          {t.facts.map((label, i) => {
            const Icon = FACT_ICONS[i];
            return (
              <div key={i} data-testid={`contact-fact-${i}`} className="flex items-center gap-2.5 text-sm text-stone-400">
                <Icon className="w-4 h-4 text-[#C5A059]" />
                {label}
              </div>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          data-testid="service-map-block"
          className="mt-16 max-w-5xl mx-auto"
        >
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <h3 className="font-serif-display font-semibold text-2xl sm:text-3xl text-stone-100 flex items-center gap-3">
                <Navigation className="w-5 h-5 text-[#C5A059]" />
                {t.mapTitle}
              </h3>
              <p className="mt-2 text-sm text-stone-400">{t.mapSub}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {t.mapChips.map((chip) => (
                <span key={chip} className="rounded-full bg-[#2E2720] border border-stone-800 px-3.5 py-1.5 text-[11px] font-medium text-stone-300">
                  {chip}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden border border-stone-800 shadow-xl shadow-black/40">
            <iframe
              data-testid="service-map"
              title="Kumar Taxi & Tours Salzburg — Map"
              src={MAP_EMBED}
              className="w-full h-[380px] border-0"
              style={{ filter: "invert(0.9) hue-rotate(180deg) saturate(0.65) brightness(0.95)" }}
              loading="lazy" decoding="async"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
